import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Blocks, Plus, Search, FolderPlus, Download, Trash2, PackageOpen } from 'lucide-react';

import { useSession } from '../../app/useSession';
import { useTeamMember } from '../../app/useTeamMember';
import { useToast } from '../../components/Toast';
import { cn } from '../../lib/cn';
import {
  useSkillCategories,
  useSkills,
  useUpsertCategory,
  useDeleteCategory,
  useUpsertSkill,
  type Skill,
  type SkillCategory,
} from './skillsQueries';
import { useImportRepoPack, REPO_PACK_SKILLS } from './importRepoPack';
import { slugify } from './slug';
import styles from './skills.module.css';

const STATUS_LABEL: Record<Skill['status'], string> = {
  draft: 'טיוטה',
  published: 'פורסם',
  archived: 'בארכיון',
};

export function SkillsPage() {
  const session = useSession();
  const sessionObj = session.status === 'signed-in' ? session.session : null;
  const userId = sessionObj?.user.id ?? null;
  const { data: teamMember } = useTeamMember(sessionObj);
  const orgId = teamMember?.organization_id ?? null;
  const toast = useToast();

  const { data: categories = [], isLoading: catsLoading } = useSkillCategories(orgId);
  const { data: skills = [], isLoading: skillsLoading } = useSkills(orgId);

  const upsertCategory = useUpsertCategory(orgId);
  const deleteCategory = useDeleteCategory(orgId);
  const upsertSkill = useUpsertSkill(orgId);
  const importPack = useImportRepoPack(orgId, userId);

  const [term, setTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = term.trim().toLowerCase();
    return skills.filter((s) => {
      if (activeCategory && s.category_id !== activeCategory) return false;
      if (!q) return true;
      return (
        s.name.toLowerCase().includes(q) ||
        s.slug.toLowerCase().includes(q) ||
        (s.description ?? '').toLowerCase().includes(q) ||
        s.tags.some((t) => t.toLowerCase().includes(q))
      );
    });
  }, [skills, term, activeCategory]);

  const countFor = (categoryId: string | null) =>
    skills.filter((s) => s.category_id === categoryId).length;

  async function addCategory() {
    const name = window.prompt('שם הנושא');
    if (!name?.trim()) return;
    try {
      await upsertCategory.mutateAsync({
        id: null,
        values: {
          slug: slugify(name),
          name: name.trim(),
          description: null,
          icon: null,
          sort_order: categories.length,
        },
      });
      toast.success('הנושא נוסף');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'הוספת הנושא נכשלה');
    }
  }

  async function removeCategory(category: SkillCategory) {
    const used = countFor(category.id);
    const warning =
      used > 0
        ? `בנושא הזה ${String(used)} סקילים. הם לא יימחקו, הם יעברו ל"ללא נושא". להמשיך?`
        : 'למחוק את הנושא?';
    if (!window.confirm(warning)) return;
    try {
      await deleteCategory.mutateAsync(category.id);
      if (activeCategory === category.id) setActiveCategory(null);
      toast.success('הנושא נמחק');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'מחיקת הנושא נכשלה');
    }
  }

  async function addSkill() {
    const name = window.prompt('שם הסקיל');
    if (!name?.trim()) return;
    try {
      await upsertSkill.mutateAsync({
        id: null,
        values: {
          category_id: activeCategory,
          slug: slugify(name),
          name: name.trim(),
          description: null,
          status: 'draft',
          version: '0.1.0',
          tags: [],
        },
      });
      toast.success('הסקיל נוצר. הוסף לו קבצים.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'יצירת הסקיל נכשלה');
    }
  }

  async function runImport() {
    try {
      const added = await importPack.mutateAsync();
      toast.success(`יובאו ${String(added)} סקילים מהריפו`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'הייבוא נכשל');
    }
  }

  const loading = catsLoading || skillsLoading;
  const empty = !loading && skills.length === 0;

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <h1>Skills</h1>
          <p className={styles.sub}>
            ספריית הסקילים שלך, לפי נושאים. כאן בונים, עורכים ומורידים אותם לכל פרויקט.
          </p>
        </div>
        <Blocks size={32} className={styles.headIcon} aria-hidden="true" />
      </header>

      <div className={styles.toolbar}>
        <div className={styles.search}>
          <Search size={15} aria-hidden="true" />
          <input
            type="search"
            value={term}
            onChange={(e) => { setTerm(e.target.value); }}
            placeholder="חיפוש לפי שם, slug או תגית"
            aria-label="חיפוש סקילים"
          />
        </div>
        <div className={styles.toolbarActions}>
          <button type="button" className={styles.btnSecondary} onClick={() => void addCategory()}>
            <FolderPlus size={15} aria-hidden="true" />
            נושא חדש
          </button>
          <button type="button" className={styles.btnPrimary} onClick={() => void addSkill()}>
            <Plus size={15} aria-hidden="true" />
            סקיל חדש
          </button>
        </div>
      </div>

      {empty && (
        <div className={styles.emptyState}>
          <PackageOpen size={28} aria-hidden="true" />
          <h2>עדיין אין סקילים</h2>
          <p>
            אפשר להתחיל מסקיל ריק, או לייבא את ערכת העברית שכבר יושבת בריפו תחת{' '}
            <code dir="ltr">skills projects/</code>. הייבוא יוצר {REPO_PACK_SKILLS} סקילים
            בנושא אחד, ואפשר לערוך אותם אחר כך כמו כל סקיל.
          </p>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={() => void runImport()}
            disabled={importPack.isPending}
          >
            <Download size={15} aria-hidden="true" />
            {importPack.isPending ? 'מייבא…' : 'ייבא את ערכת העברית מהריפו'}
          </button>
        </div>
      )}

      {!empty && (
        <div className={styles.layout}>
          <nav className={styles.categories} aria-label="נושאים">
            <button
              type="button"
              className={activeCategory === null ? styles.catActive : styles.cat}
              onClick={() => { setActiveCategory(null); }}
            >
              <span>הכול</span>
              <span className={styles.catCount}>{skills.length}</span>
            </button>
            {categories.map((category) => (
              <div key={category.id} className={styles.catRow}>
                <button
                  type="button"
                  className={activeCategory === category.id ? styles.catActive : styles.cat}
                  onClick={() => { setActiveCategory(category.id); }}
                >
                  <span>{category.name}</span>
                  <span className={styles.catCount}>{countFor(category.id)}</span>
                </button>
                <button
                  type="button"
                  className={styles.iconBtn}
                  onClick={() => void removeCategory(category)}
                  aria-label={`מחק את הנושא ${category.name}`}
                >
                  <Trash2 size={14} aria-hidden="true" />
                </button>
              </div>
            ))}
            {countFor(null) > 0 && (
              <button
                type="button"
                className={styles.cat}
                onClick={() => { setActiveCategory(null); }}
                disabled
              >
                <span className={styles.catMuted}>ללא נושא</span>
                <span className={styles.catCount}>{countFor(null)}</span>
              </button>
            )}
          </nav>

          <div className={styles.grid}>
            {loading && <p className={styles.muted}>טוען…</p>}
            {!loading && filtered.length === 0 && (
              <p className={styles.muted}>אין סקילים שתואמים לחיפוש.</p>
            )}
            {filtered.map((skill) => (
              <Link key={skill.id} to={`/skills/${skill.slug}`} className={styles.card}>
                <div className={styles.cardHead}>
                  <h3>{skill.name}</h3>
                  <span className={cn(styles.badge, styles[`badge_${skill.status}`])}>
                    {STATUS_LABEL[skill.status]}
                  </span>
                </div>
                <p className={styles.cardBody}>{skill.description ?? 'בלי תיאור עדיין.'}</p>
                <div className={styles.cardFoot}>
                  <span className={styles.path} dir="ltr">
                    {skill.slug}
                  </span>
                  <span className={styles.version} dir="ltr">
                    v{skill.version}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
